import { useStorage } from "@vueuse/core";

const userLocale = useStorage("locale", "en-US");

interface NumberFormatArguments {
	value: number;
	locale?: string;
	options?: Intl.NumberFormatOptions;
}

export const formatNumber = ({ value, locale, options = {} }: NumberFormatArguments) => {
	return new Intl.NumberFormat(locale ?? userLocale.value, options).format(value);
};

const formatCurrency = ({ value, locale, options = {} }: NumberFormatArguments) => {
	return new Intl.NumberFormat(locale ?? userLocale.value, {
		style: "currency",
		currency: "USD",
		notation: "standard",
		...options,
	}).format(value);
};

const formatDecimal = ({ value, locale, options = {} }: NumberFormatArguments) => {
	return new Intl.NumberFormat(locale ?? userLocale.value, {
		style: "decimal",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
		...options,
	}).format(value);
};

const formatInteger = ({ value, locale, options = {} }: NumberFormatArguments) => {
	return new Intl.NumberFormat(locale ?? userLocale.value, {
		style: "decimal",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
		...options,
	}).format(value);
};

const formatPercent = ({ value, locale, options = {} }: NumberFormatArguments) => {
	return new Intl.NumberFormat(locale ?? userLocale.value, {
		style: "percent",
		useGrouping: false,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
		...options,
	}).format(value);
};

export const formatBandwidth = ({ value, locale, options = {} }: NumberFormatArguments) => {
	const { unit, unitValue } = ((value: number) => {
		if (value < 1024) {
			return { unit: "byte", unitValue: value };
		}
		if (value < 1024 * 1024) {
			return { unit: "kilobyte", unitValue: Math.round(value / 1024) };
		}
		if (value < 1024 * 1024 * 1024) {
			return { unit: "megabyte", unitValue: Math.round(value / (1024 * 1024)) };
		}
		if (value < 1024 * 1024 * 1024 * 1024) {
			return {
				unit: "gigabyte",
				unitValue: Math.round(value / (1024 * 1024 * 1024)),
			};
		}
		if (value < 1024 * 1024 * 1024 * 1024 * 1024) {
			return {
				unit: "terabyte",
				unitValue: Math.round(value / (1024 * 1024 * 1024 * 1024)),
			};
		}

		return {
			unit: "petabyte",
			unitValue: Math.round(value / (1024 * 1024 * 1024 * 1024 * 1024)),
		};
	})(value);

	return new Intl.NumberFormat(locale ?? userLocale.value, {
		unit: unit,
		style: "unit",
		// maximumSignificantDigits: 4,
		// minimumSignificantDigits: 4,
		...options,
	}).format(unitValue);
};

export const formatBandwidthFromKb = ({ value, locale, options = {} }: NumberFormatArguments) => {
	return formatBandwidth({ value: value * 1024, locale, options });
};

export const formatBandwidthFromMb = ({ value, locale, options = {} }: NumberFormatArguments) => {
	return formatBandwidth({ value: value * 1024 * 1024, locale, options });
};
